/*!
  Pioneer WFRIA ListView v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["ListView"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("ListView") < 0) {
    window.__PIONEER_GLOBAL__.components.push("ListView");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["paginator", "selectable", "data", "customStrings", "totalItems", "itemKey", "expandable", "emptyState"],
      _excluded2 = ["selectedItems", "defaultSelectedItems", "onSelectedChange"],
      _excluded3 = ["expandedItems", "defaultExpandedItems"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import PropTypes from "prop-types";
import React from "react";
import Composer from "../common/Composer";
import { deprecated } from "../common/proptypes";
import { getObjectKeyFn } from "../common/utilities";
import ListViewUI from "./ListViewUI";
import List, { ALL_PAGES_SELECTION, ITEM_SELECTION, PAGE_SELECTION } from "./renderProps/List";
import Pagination from "./renderProps/Pagination";
import STRINGS from "./strings";
import { requiredIfRemotePagination, validateControlPropsCallbacks, validatePropsForRemotePagination } from "./utils";
/**
 * ListView is a React component that renders a list of different types of data
 * whose content does not lend itself well to display in tabular format.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
const ListView = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    paginator,
    selectable,
    data,
    customStrings,
    totalItems,
    itemKey,
    expandable,
    emptyState
  } = props,
        otherProps = _objectWithoutProperties(props, _excluded);

  const componentStrings = _objectSpread(_objectSpread(_objectSpread({}, STRINGS), customStrings), {}, {
    noResults: _objectSpread(_objectSpread({}, STRINGS.noResults), customStrings.noResults)
  });

  const getItemKey = getObjectKeyFn(itemKey);

  const onExpandedChange = _ref => {
    let {
      items,
      select: expand,
      type
    } = _ref;
    const {
      onExpandedChange
    } = expandable;

    if (onExpandedChange) {
      onExpandedChange({
        items,
        expand,
        type
      });
    }
  };

  const getRenderProps = () => {
    const renderProps = [];

    if (paginator) {
      renderProps.push({
        paginator: _jsx(Pagination, _objectSpread({
          data: data,
          totalItems: totalItems
        }, paginator))
      });
    }

    if (selectable) {
      const selectableAsObject = typeof selectable === "boolean" ? {} : selectable;

      const {
        selectedItems,
        defaultSelectedItems,
        onSelectedChange
      } = selectableAsObject,
            selectableProps = _objectWithoutProperties(selectableAsObject, _excluded2);

      if (selectedItems) {
        selectableProps.items = selectedItems;
      } else {
        selectableProps.defaultItems = defaultSelectedItems;
      }

      renderProps.push({
        selectable: (_ref2 // eslint-disable-line no-unused-vars
        ) => {
          let {
            results: {
              paginator: paginatorResult
            },
            render
          } = _ref2;
          return _jsx(List, _objectSpread(_objectSpread({
            data: data,
            remotePagination: paginator && paginator.remote,
            currentPageData: paginator ? paginatorResult.paginatedData : data,
            getItemKey: getItemKey,
            totalItems: totalItems,
            onChange: onSelectedChange
          }, selectableProps), {}, {
            children: render
          }));
        }
      });
    }

    if (expandable) {
      const expandableAsObject = typeof expandable === "boolean" ? {} : expandable;
      const {
        areAllRowsExpanded
      } = expandable;

      const {
        expandedItems,
        defaultExpandedItems
      } = expandableAsObject,
            expandableProps = _objectWithoutProperties(expandableAsObject, _excluded3);

      expandableProps.allExpanded = areAllRowsExpanded;

      if (expandedItems) {
        expandableProps.items = expandedItems;
      } else {
        expandableProps.defaultItems = defaultExpandedItems;
      }

      renderProps.push({
        expandable: _jsx(List, _objectSpread({
          data: data,
          remotePagination: paginator && paginator.remote,
          getItemKey: getItemKey,
          totalItems: totalItems,
          onChange: onExpandedChange
        }, expandableProps))
      });
    }

    return renderProps;
  };

  validatePropsForRemotePagination(props);
  validateControlPropsCallbacks(props);
  const renderProps = getRenderProps();

  const listViewComposed = _jsx(Composer, {
    components: renderProps,
    children: _ref3 => {
      let {
        paginator: paginatorResult,
        selectable,
        expandable
      } = _ref3;
      return _jsx(ListViewUI, _objectSpread({
        ref: ref
      }, _objectSpread(_objectSpread({}, otherProps), {}, {
        paginator: paginatorResult,
        selectable,
        expandable,
        data,
        getItemKey,
        totalItems: totalItems ? totalItems : data.length,
        customStrings: componentStrings,
        emptyState
      })));
    }
  });

  return listViewComposed;
});
/** Selection types */

ListView.SELECTION_TYPES = {
  ITEM: ITEM_SELECTION,
  PAGE: PAGE_SELECTION,
  ALL_PAGES: ALL_PAGES_SELECTION
};
/** Expansion types */

ListView.EXPANSION_TYPES = {
  ITEM: ITEM_SELECTION,
  ALL_PAGES: ALL_PAGES_SELECTION
};
ListView.propTypes = {
  /** The `aria-label` for the ListView container. */
  "aria-label": PropTypes.string.isRequired,

  /**
   * A [render prop](https://reactjs.org/docs/render-props.html) that returns a [ListViewItem](#/Component%20helpers/ListViewItem).
   * It receives the following arguments:
   *
   * <ul>
   *  <li>
   *    `item`: Item's data
   *  </li>
   *  <li>
   *    `itemKey`: Unique item id
   *  </li>
   *  <li>
   *    `itemNumber`: Item's number
   *  </li>
   *  <li>
   *    `isExpanded`: Boolean indicating if the item is expanded
   *  </li>
   *  <li>
   *    `isSelected`: Boolean indicating if the item is selected
   *  </li>
   *  <li>
   *    `onExpand`: Callback function that will expand the item
   *  </li>
   *  <li>
   *    `onSelect`: Callback function that will select the item
   *  </li>
   * </ul>
   */
  children: PropTypes.func.isRequired,

  /** Override for strings used within the component. */
  customStrings: PropTypes.shape({
    /**
     * DEPRECATED. Use `checkboxAriaLabelSelect` instead.
     */
    select: deprecated(PropTypes.string, `Use "checkboxAriaLabelSelect" instead`),

    /**
     * aria-label for the "Select All" checkbox.
     * If it is a function, it receives selected count as an argument.
     * Default to "Select All Checkbox, ${selectedCount} items selected"
     */
    selectAll: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

    /**
     * aria-label for the select item checkbox.
     * It receives an object containing the row data as an argument.
     * Defaults to `Select Item ${item.id}`
     */
    checkboxAriaLabelSelect: PropTypes.func,

    /**
     * aria-label for the item's expand button when in expanded state.
     * Default to "Collapse"
     */
    collapse: PropTypes.string,

    /**
     * aria-label for the "Collapse All" button when in expanded state.
     * If it is a function, it receives row count as an argument.
     * Defaults to "Collapse All ${rowsCount} rows"
     */
    collapseAll: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

    /**
     * aria-label for the item's expand button when in collapsed state.
     * Default to "Expand"
     */
    expand: PropTypes.string,

    /**
     * aria-label for the "Expand All" button when in collapsed state.
     * If it is a function, it receives row count as an argument.
     * Defaults to "Expand All ${rowsCount} rows"
     */
    expandAll: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

    /**
     * A function that returns the message for the collapsed row.
     * This message is set inside a visually hidden node, to be read by the
     * screen reader, when Collapse Row button is triggered.
     * It will receive following arguments:
     *
     * <ul>
     *  <li>`itemKey`: row id.</li>
     *  <li>`item`: row data.</li>
     * </ul>
     * Defaults to `more about ${itemKey}, collapsed`
     */
    collapsedRowAnnouncement: PropTypes.func,

    /**
     * A function that returns the message for the expanded row.
     * This message is set inside a visually hidden node, to be read by the
     * screen reader, when Expand Row button is triggered.
     * It will receive following arguments:
     *
     * <ul>
     *  <li>`itemKey`: row id.</li>
     *  <li>`item`: row data.</li>
     * </ul>
     * Defaults to `more about ${itemKey}, expanded`
     */
    expandedRowAnnouncement: PropTypes.func,

    /**
     * A function that returns the message for the number of rows in expanded state.
     * This message is set inside a visually hidden live region node,
     * to be read by the screen readers, when Expand All button is triggered.
     * It receives row count as an argument.
     * Defaults to "${rowsCount} rows expanded"
     */
    expandAllRowsAnnouncement: PropTypes.func,

    /**
     * A function that returns the message for the number of rows in collapsed state.
     * This message is set inside a visually hidden live region node,
     * to be read by the screen readers, when Collapse All button is triggered.
     * It receives row count as an argument.
     * Defaults to "${rowsCount} rows collapsed"
     */
    collapseAllRowsAnnouncement: PropTypes.func,

    /**
     * aria-label for Expandable button of a single row.
     * This function will receive two arguments.
     * First argument is a unique key that represents that single row and
     * second argument is an object containing the following properties:
     * <ul>
     *  <li>
     *    `item`: Row's item data.
     *  </li>
     *  <li>
     *    `expanded`: Row's expanded state.
     *  </li>
     * <li>
     *    `itemNumber`: Row's item number.
     *  </li>
     * </ul>
     */
    rowExpandButtonLabel: PropTypes.func,

    /**
     * Label used in the item number visually hidden screen reader message.
     * Defaults to "Item"
     */
    item: PropTypes.string,

    /**
     * String used for the option to unselect all items.
     * Default: "Unselect all items".
     */
    unselectAllItems: PropTypes.string,

    /**
     * String used for the option to unselect all items on the page.
     * Default: "Unselect all on this page".
     */
    unselectAllOnThisPage: PropTypes.string,

    /**
     * String used in the first option of select all button, along with
     * count of the selected items. Default: "items selected".
     */
    itemsSelected: PropTypes.string,

    /**
     * String used for the option to select all items.
     * Default: "Select all items".
     */
    selectAllItems: PropTypes.string,

    /**
     * String used for the option to select all items on the page.
     * Default: "Select all on this page".
     */
    selectAllOnThisPage: PropTypes.string,

    /**
     * String used in the `aria-label` of the select all button.
     * Default: "Select All,".
     */
    selectAllButton: PropTypes.string,

    /**
     * DEPRECEATED and replaced with EmptyState
     * Message used for EmptyState when no ListViewItems are visible.
     * The title and/or the body of the message can be customized.
     */
    noResults: PropTypes.shape({
      /**
       * Title used for EmptyState when no ListViewItems are visible.
       * Defaults to "No Results."
       */
      title: PropTypes.string,

      /**
       * Body for EmptyState when no ListViewItems are visible.
       * Defaults to "Change your criteria and try again."
       */
      body: PropTypes.string
    })
  }),

  /** An array of objects which are considered the items to display. */
  data: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types

  /**
   * Used to customize the EmptyState displayed when there is no data.
   */
  emptyState: PropTypes.shape({
    /**
     * Message text.
     */
    message: PropTypes.string,

    /**
     * Button or Anchor call to action.
     */
    actionElement: PropTypes.node,

    /**
     * Aligment of EmptyState contents.
     */
    alignContent: PropTypes.oneOf(["left", "center"]),

    /**
     * Header text with default h2 or header text with heading tag.
     */
    emptyStateHeader: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

    /**
     * Id for message text.
     */
    messageId: PropTypes.string,

    /**
     * If true, the illustration won't be present.
     */
    noIllustration: PropTypes.bool
  }),

  /**
   * It makes items expandable. It can be either a boolean or an object with these config options:
   *
   * <ul>
   *  <li>
   *    `defaultExpandedItems`: Array of item keys to be expanded on initial render.
   *  </li>
   *  <li>
   *    `disabledItems`: Array of item keys to be disabled. These items won't be expanded or collapsed.
   *  </li>
   *  <li>
   *    `expandedItems`: Array of item keys to be expanded. Required if `paginator.remote` is `true`.
   *  </li>
   *  <li>
   *    `onExpandedChange`: Callback that fires when items are expanded or collapsed.
   *    Required if `paginator.remote` is `true` or `expandedItems` is passed. It receives an object:
   *
   *    <ul>
   *      <li>`items`: Item keys to be expanded/collapsed.</li>
   *      <li>`expand`: `true` if items are expanded and `false` if items are collapsed.</li>
   *      <li>`type`: Indicates whether the expansion state is changed per item or across all pages:
   *        `ListView.EXPANSION_TYPES.ITEM`,
   *        `ListView.EXPANSION_TYPES.ALL_PAGES`
   *      </li>
   *    </ul>
   *  </li>
   *  <li>
   *    `areAllRowsExpanded`: To determine if the "Expand All" button is expanded or not.
   *    It expects a boolean as a return value. This is meant to be used when dealing
   *    with a controlled ListView in certain scenarios (e.g. server side pagination).
   *  </li>
   * </ul>
   */
  expandable: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({
    defaultExpandedItems: PropTypes.array,
    // eslint-disable-line react/forbid-prop-types
    expandedItems: PropTypes.array,
    // eslint-disable-line react/forbid-prop-types
    disabledItems: PropTypes.array,
    // eslint-disable-line react/forbid-prop-types
    onSelectedChange: PropTypes.func,
    areAllRowsExpanded: PropTypes.bool
  })]),

  /**
   * A render prop or a node that renders the footer. The render prop receives an object with
   * a `selectedItems` prop, if `selectable` is passed.
   */
  footer: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),

  /**
   * A render prop or a node that renders the header. The render prop receives an object with
   * a `selectedItems` prop, if `selectable` is passed.
   */
  header: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),

  /**
   * A string that references a unique id among the items,
   * or a function that generates a unique id for each item.
   * If not passsed, it will default to `id`.
   */
  itemKey: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),

  /**
   * It shows the item number in each item's header. It can be either a boolean or
   * an object with an `offset` property.
   */
  itemNumbers: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({
    offset: PropTypes.number
  })]),

  /** Check [Paginator](#/Components/Paginator) for the full list of props. */
  paginator: PropTypes.shape({
    /**
     * Container where Paginator component will be rendered.
     * It expects a [ref](https://reactjs.org/docs/refs-and-the-dom.html).
     */
    container: PropTypes.shape({
      current: PropTypes.instanceOf(Element)
    }).isRequired,

    /** Class name for the Paginator component. */
    className: PropTypes.string,

    /** Currently viewed page on initial render. Only positive numbers are applicable. */
    defaultPage: PropTypes.number,

    /**
     * Number of items to be displayed in a page on initial render.
     * Only positive numbers are applicable.
     */
    defaultItemsPerPage: PropTypes.number,

    /** Currently viewed page. Only positive numbers are applicable. */
    page: requiredIfRemotePagination(PropTypes.number, "paginator.page"),

    /** Number of items to be displayed in a page. Only positive numbers are applicable. */
    itemsPerPage: requiredIfRemotePagination(PropTypes.number, "paginator.itemsPerPage"),

    /**
     * Callback that will be executed when user navigates across pages.
     * It receives the following arguments new page number and items per page as arguments:
     * <ul>
     *   <li>`page`: new page number.</li>
     *   <li>`itemsPerPage`: items per page.</li>
     * </ul>
     */
    onPagination: requiredIfRemotePagination(PropTypes.func, "paginator.onPagination"),

    /**
     * It indicates if pagination should be done by `ListView` or outside
     * of it (e.g. server side pagination).
     */
    remote: PropTypes.bool,

    /**
     * Use this prop to set the position of the tooltips for the next
     * and previous buttons of the Paginator. This will be an object value
     * for independent positioning of the tooltips. The capability to accept
     * a single string value is maintained to not break the previous API.
     * If a string value is received, a default of previous: "top-right",
     * next: "bottom-right" will be applied.
     */
    navigationTooltipPosition: PropTypes.oneOfType([PropTypes.oneOf(["top-right", "top-center", "bottom-left", "bottom-center", "bottom-right", "left", "right"]), PropTypes.shape({
      previous: PropTypes.oneOf(["top-right", "top-center", "bottom-left", "bottom-center", "bottom-right", "left", "right"]),
      next: PropTypes.oneOf(["top-right", "top-center", "bottom-left", "bottom-center", "bottom-right", "left", "right"])
    })]),

    /** Sets the left and right padding around the Paginator to 0. */
    noHorizontalPadding: PropTypes.bool
  }),

  /**
   * It makes items selectable. It can be either a boolean or an object with these config options:
   *
   * <ul>
   *  <li>
   *    `selectionType`: It can receive any of these values:
   *    <ul>
   *      <li>"multi": Multiple items can be selected</li>
   *      <li>"single": Only one item can be selected</li>
   *    </ul>
   *  </li>
   *  <li>
   *    `defaultSelectedItems`: Array of item keys to be selected on initial render.
   *  </li>
   *  <li>
   *    `disabledItems`: Array of item keys to be disabled. These items can't be selected or unselected.
   *  </li>
   *  <li>
   *    `selectedItems`: Array of item keys to be selected. Required if `paginator.remote` is `true`.
   *  </li>
   *  <li>
   *    `onSelectedChange`: Callback that fires when items are selected or unselected.
   *    Required if `paginator.remote` is `true` or `selectedItems` is passed. It receives an object:
   *
   *    <ul>
   *      <li>`items`: Item keys to be selected/unselected.</li>
   *      <li>`select`: `true` for selection. `false` for unselection.</li>
   *      <li>`type`: Indicates what kind of selection/unselection was made:
   *        `ListView.SELECTION_TYPES.ITEM`,
   *        `ListView.SELECTION_TYPES.PAGE`,
   *        `ListView.SELECTION_TYPES.ALL_PAGES`
   *      </li>
   *    </ul>
   *  </li>
   * </ul>
   */
  selectable: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({
    selectionType: PropTypes.oneOf(["multi", "single"]),
    defaultSelectedItems: PropTypes.array,
    // eslint-disable-line react/forbid-prop-types
    selectedItems: PropTypes.array,
    // eslint-disable-line react/forbid-prop-types
    disabledItems: PropTypes.array,
    // eslint-disable-line react/forbid-prop-types
    onSelectedChange: PropTypes.func
  })]),

  /**
   * Total items in the data set. Required when `paginator.remote` is set to true.
   */
  totalItems: requiredIfRemotePagination(PropTypes.number, "totalItems")
};
ListView.defaultProps = {
  children: null,
  customStrings: {},
  data: [],
  emptyState: {},
  expandable: null,
  footer: null,
  header: null,
  itemKey: null,
  itemNumbers: null,
  paginator: null,
  selectable: null,
  totalItems: null
};
ListView.displayName = "ListView";
export default ListView;